import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import Avatar from '@/elements/Avatar'
import Button from '@/elements/Button'
import Label from '@/elements/Label'
import StatusCard from '@/elements/StatusCard'
import Text from '@/elements/Text'
import Flex from '@/elements/Flex'
import FormSelect from '@/elements/FormSelect'
import Modal from '@/components/Modal'
import StatusSelector from '@/components/StatusSelector'
import type { AlertResponse, AlertStatusType } from '@/api/types/Alert'
import type { DelegateResponse } from '@/api/types/Delegate'
import type { UserResponse } from '@/api/types/User'

interface AlertStatusProps {
  status: AlertStatusType
  updateStatus: (newData: Partial<AlertResponse>) => void
  assignedUser?: UserResponse
  escalatedUser?: UserResponse
  delegates: DelegateResponse[] | undefined
  disabled?: boolean
}

function AlertStatus({
  status,
  assignedUser,
  escalatedUser,
  updateStatus,
  delegates,
  disabled = false,
}: AlertStatusProps) {
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false)

  // TODO: Create an hook for form handling
  const { watch, setValue, reset: resetForm } = useForm<{
    status: AlertStatusType
    assignedTo: string | null
    escalatedTo: string | null
  }>({
    defaultValues: {
      status,
      assignedTo: assignedUser?.id || null,
      escalatedTo: escalatedUser?.id || null,
    }
  })

  useEffect(() => {
    setValue('status', status)
    setValue('assignedTo', assignedUser?.id || null)
    setValue('escalatedTo', escalatedUser?.id || null)
  }, [assignedUser, escalatedUser, setValue, status, delegates, showUpdateStatusModal])

  const watchedStatus = watch('status')
  const watchedAssignedTo = watch('assignedTo')
  const watchedEscalatedTo = watch('escalatedTo')

  const delegatesWithCurrentAssignAndEscalate = useMemo(() => {
    const updatedDelegates = delegates || []
    const delegateUserIds = updatedDelegates.map(d => d.user.id)

    if (assignedUser && !delegateUserIds.includes(assignedUser.id)) {
      updatedDelegates.push({
        status: 'ACTIVE', // Not shown in UI, so can be anything
        type: 'DELEGATE', // Not shown in UI, so can be anything
        user: {
          ...assignedUser,
          subject: '', // Not shown in UI, so can be anything
        },
      })
    }

    if (escalatedUser && !delegateUserIds.includes(escalatedUser.id) && escalatedUser?.id !== assignedUser?.id) {
      updatedDelegates.push({
        status: 'ACTIVE', // Not shown in UI, so can be anything
        type: 'DELEGATE', // Not shown in UI, so can be anything
        user: {
          ...escalatedUser,
          subject: '', // Not shown in UI, so can be anything
        },
      })
    }

    return updatedDelegates
  }, [delegates, assignedUser, escalatedUser])

  const selectedAssignedTo = delegatesWithCurrentAssignAndEscalate?.find(d => d.user.id === watchedAssignedTo) || null
  const selectedEscalatedTo = delegatesWithCurrentAssignAndEscalate?.find(d => d.user.id === watchedEscalatedTo) || null

  const hasEscalateSelect = watchedStatus === 'ESCALATED'

  const canSave = useMemo(() => {
    if (watchedStatus === 'ESCALATED') {
      if (!watchedEscalatedTo) return false
      if (watchedStatus !== status) return true
      if (watchedEscalatedTo !== (escalatedUser?.id || null)) return true
      if (watchedAssignedTo !== (assignedUser?.id || null)) return true
      return false
    }
    if (watchedStatus !== status) return true
    if (watchedAssignedTo !== (assignedUser?.id || null)) return true
    return false
  }, [assignedUser, escalatedUser, status, watchedAssignedTo, watchedEscalatedTo, watchedStatus])

  const handleStatusUpdate = () => {
    const dataToSave: Partial<AlertResponse> & {
      assignedToId?: string | null
      escalateToId?: string | null
    } = {}

    if (watchedStatus !== status) {
      dataToSave.status = watchedStatus
      if (watchedStatus === 'ESCALATED') {
        dataToSave.escalateToId = watchedEscalatedTo
      }
    }
    if (watchedAssignedTo !== assignedUser?.id) {
      dataToSave.assignedToId = watchedAssignedTo
    }
    updateStatus(dataToSave)
    setShowUpdateStatusModal(false)
  }

  return (
    <>
      <StatusCard
        status={status}
        onClick={() => setShowUpdateStatusModal(true)}
      />
      <Flex>
        <Label>Assigned To</Label>
        <Flex inline align='center' gap={2}>
          <Avatar
            name={assignedUser?.details.fullName || ''}
            src={assignedUser?.assets.PROFILE_PICTURE || ''}
            size='small'
          />
          <Flex>
            <Text variant='h4' color={assignedUser ? 'black' : 'black-light-40'}>
              {assignedUser?.details.fullName || 'Unassigned'}
            </Text>
            {/* <Label>{formatAsTitle(assignedUser?.organization || '')}</Label> */}
          </Flex>
        </Flex>
      </Flex>
      {
        escalatedUser ? (
          <Flex>
            <Label>Escalated To</Label>
            <Flex inline align='center' gap={2}>
              <Avatar
                name={escalatedUser?.details.fullName || ''}
                src={escalatedUser?.assets.PROFILE_PICTURE || ''}
                size='small'
              />
              <Flex>
                <Text variant='h4' color={escalatedUser ? 'black' : 'black-light-40'}>
                  {escalatedUser?.details.fullName || 'Unassigned'}
                </Text>
                {/* <Label>{getRoleName(escalatedUser?.type)}</Label> */}
              </Flex>
            </Flex>
          </Flex>
        ) : null
      }
      {disabled ? null : (
        <Button
          variant='primary'
          onClick={() => setShowUpdateStatusModal(true)}
          styles={{ width: '100%' }}
          disabled={disabled}
        >
          Update Status
        </Button>
      )}
      {showUpdateStatusModal && (
        <Modal
          title='Update Status'
          isOpen={showUpdateStatusModal}
          onCancel={() => {
            setShowUpdateStatusModal(false)
            resetForm()
          }}
          onSave={handleStatusUpdate}
          saveLabel='Update Status'
          disabled={!canSave}
        >
          <Flex wfull gap={4}>
            <StatusSelector
              selectedStatus={watchedStatus}
              onSelect={s => setValue('status', s)}
            />
            <FormSelect<DelegateResponse>
              name='assignedTo'
              label='Assigned To'
              placeholder='Select a user'
              values={selectedAssignedTo ? [selectedAssignedTo] : []}
              options={delegatesWithCurrentAssignAndEscalate || []}
              optionLabel={item => item.user.details.fullName}
              optionValue={item => item.user.id}
              optionAvatar={item => item.user.assets.PROFILE_PICTURE || ''}
              onChange={([v]) => setValue('assignedTo', v ? v.user.id : null)}
              style={{ maxWidth: '100%', width: '100%' }}
              filterBy={(item, search) =>
                item.user.details.fullName.toLowerCase().includes(search.toLowerCase())
              }
            />
            {hasEscalateSelect && (
              <FormSelect<DelegateResponse>
                name='escalatedTo'
                label='Escalated To'
                placeholder='Select a user'
                values={selectedEscalatedTo ? [selectedEscalatedTo] : []}
                options={delegatesWithCurrentAssignAndEscalate || []}
                optionLabel={item => item.user.details.fullName}
                optionValue={item => item.user.id}
                optionAvatar={item => item.user.assets.PROFILE_PICTURE || ''}
                onChange={([v]) => setValue('escalatedTo', v ? v.user.id : null)}
                style={{ maxWidth: '100%', width: '100%' }}
                filterBy={(item, search) =>
                  item.user.details.fullName.toLowerCase().includes(search.toLowerCase())
                }
              />
            )}
          </Flex>
        </Modal>
      )}
    </>
  )
}

export default AlertStatus
